import { toaster } from '@/components/ui/toaster';
import { brandDnaQueryKey, type BrandDnaData } from '@/hooks/queries/use-brand-dna-query';
import { useCurrentBrandQuery } from '@/hooks/queries/use-current-brand-query';
import api from '@/libs/axiosInstance';
import type { ApiResponse } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

interface UpdateBrandDnaDto {
  dna: string;
  sourceUrl?: string;
}

/** Saves a Brand DNA (`PUT /dashboard/brands/me/dna`), without any toast. */
export async function saveBrandDna(data: UpdateBrandDnaDto): Promise<ApiResponse> {
  const response = await api.put('/dashboard/brands/me/dna', data);
  return response.data;
}

export function useUpdateBrandDnaAction() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { data: brand } = useCurrentBrandQuery();
  const brandId = brand?.id ?? '';

  return useMutation<ApiResponse, Error, UpdateBrandDnaDto>({
    mutationFn: saveBrandDna,
    onSuccess: (_response, variables) => {
      if (brandId) {
        // Reflect the saved DNA in the cache synchronously so consumers that
        // gate on `hasDna` (the companion wizard) see it immediately, without
        // waiting for the invalidation refetch. The refetch still runs to
        // reconcile server-side fields (updatedAt).
        queryClient.setQueryData<BrandDnaData>(brandDnaQueryKey(brandId), (prev) => ({
          brandDna: variables.dna,
          brandDnaSourceUrl: variables.sourceUrl ?? prev?.brandDnaSourceUrl ?? null,
          brandDnaUpdatedAt: new Date().toISOString(),
        }));
        queryClient.invalidateQueries({ queryKey: brandDnaQueryKey(brandId) });
      }
      queryClient.invalidateQueries({ queryKey: ['currentBrand'] });

      toaster.success({
        title: t('settings.brandDna.toast.successTitle'),
        description: t('settings.brandDna.toast.successDescription'),
      });
    },
    onError: () => {
      toaster.error({
        title: t('settings.brandDna.toast.errorTitle'),
        description: t('settings.brandDna.toast.errorDescription'),
      });
    },
  });
}
